import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Layout from '@/components/Layout/Layout';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { PageInfo } from '@/interfaces/common.interface';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { checkIsExpire, GetFilterUrl, showToastNotification, toFixedThree } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import StatusDropdown from '@/components/common/StatusComponent';
import ImagePreview from '@/components/common/media/ImagePreview';
import { Star } from 'lucide-react';
import Pagination from '@/components/common/Pagination';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import ErrorMessage from '@/components/common/ErrorMessage';
import { errorTypes } from '@/utility/validation';

interface AddProductDiscountProp {
  id: string[];
  discountPercentage: number;
  discountStartDate: string;
  discountEndDate: string;
  type: 'add';
  label: string
}

interface DiscountStatesProp {
  isLoading: boolean
  products: IproductResponse[];
  pageInfo: PageInfo;
}

const AddProductDiscount = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const [states, setStates] = useState<DiscountStatesProp>({
    isLoading: false,
    products: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const { register, setValue, watch, handleSubmit, formState: { errors } } = useForm<AddProductDiscountProp>({
    defaultValues: {
      id: [],
      discountPercentage: 1,
      discountStartDate: '',
      discountEndDate: '',
      type: 'add',
    },
  });

  const discountStartDate = watch(`discountStartDate`);
  const discountEndDate = watch(`discountEndDate`);

  useEffect(() => {
    if (!discountStartDate || !discountEndDate) {
      setDateError("Discount dates is required");
      return;
    }

    const start = new Date(discountStartDate);
    const end = new Date(discountEndDate);

    if (end < start) {
      setDateError('End date cannot be earlier than start date.');
    } else if (end.getTime() === start.getTime()) {
      setDateError('Start date and end date cannot be the same.');
    } else {
      setDateError(null);
    }
  }, [discountStartDate, discountEndDate]);


  const searchProducts = useCallback(async (name: string) => {
    const controller = new AbortController();
    try {
      handleChangeState({ isLoading: true });
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        search: name,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query ? `${query}` : ''}`;

      const res = await callApi<IproductResponseList>(PATH, {
        method: 'GET',
        signal: controller.signal,
      });

      handleChangeState({
        isLoading: false,
        products: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
      });
    } catch (error: any) {
      handleChangeState({ isLoading: false });
      if (error.name !== 'AbortError') {
        console.log('error is', error);
      }
    }

    return () => controller.abort();
  }, [states.pageInfo.page, states.pageInfo.limit]);

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      const abort = await searchProducts(searchInput);
      return () => abort();
    }, 500);

    return () => {
      clearTimeout(delayDebounce);
    };
  }, [searchInput, states.pageInfo.page, states.pageInfo.limit, searchProducts]);


  const { selectedItems, handleSelectAll, handleSelectItem } = useSelectableTable(states.products);

  useEffect(() => {
    console.log('value is the value')
    setValue('id', Array.from(selectedItems).map(String))
  }, [selectedItems, setValue])

  // useEffect(() => {
  //   const delayDebounce = setTimeout(() => {
  //     searchProducts(searchInput);
  //   }, 500);
  //   return () => clearTimeout(delayDebounce);
  // }, [searchInput, states.pageInfo.page, states.pageInfo.limit, searchProducts]);

  const onSubmit = async (data: AddProductDiscountProp) => {
    setIsLoading(true);
    try {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/create/discount`;
      await callApi(PATH, { method: 'POST', data });
      setIsLoading(false);
      navigate(RoutesLink.discounts);
    } catch (error: unknown) {
      const err = error as { message?: string };
      if (err.message) showToastNotification('danger', err.message);
      setIsLoading(false);
      console.log('error in discount upload', error);
    }
  };

  const columns: TableColumn<IproductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => {
        const url = item?.directImageUrl ? item?.directImageUrl : item?.media?.[0]?.url
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${url}`}
            alt={''}
            text={item.name}
          />
        )
      },
      className: 'p-3',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item.status}
          options={[]}
          onChange={() => { }}
          canEditVal="product"
        />
      ),
    },
    {
      key: 'stock',
      header: 'Stock',
      render: (item) => item.productInfo[0]?.quantity || 0,
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (item) => (
        <span>
          {checkIsExpire(item?.productInfo[0]?.discountEndDate) ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'ItemCode',
      header: 'Item Code',
      render: (item) => (
        <span>
          {item?.itemCode || '--'}
        </span>
      ),
    },
    {
      key: 'ItemId',
      header: 'Item Id',
      render: (item) => (
        <span>
          {item?.inventoryItemId}
        </span>
      ),
    },
    {
      key: 'averageRating',
      header: 'Rating',
      render: (item) => (
        <div className="p-3 flex items-center gap-1">
          {toFixedThree(item?.averageRating || 0, 1)}
          <Star size={16} className="text-orange-400 fill-orange-500" />
        </div>
      ),
    },
  ];

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className='space-y-6'>
          <Card>
            <h2 className="text-lg font-semibold mb-3">Discount Details</h2>
            <div className='mb-3'>
              <FormField
                label="Label"
                placeholder="EID Special"
                min={0}
                {...register(`label`, {
                  ...errorTypes.required('Discount Label')
                })}
              />
              <ErrorMessage message={errors.label?.message} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField
                placeholder="Select discounted products"
                value={searchInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
                label='Search Products'
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <FormField
                label="Enter Discount"
                placeholder="Select discounted products"
                suffix="%"
                min={1}
                {...register('discountPercentage', {
                  valueAsNumber: true,
                  required: 'Discount is required'
                })}
              />
            </div>
          </Card>
          <Card>
            <h2 className="text-lg font-semibold mb-3">Selecte Products</h2>
            <CommonTable
              columns={columns}
              data={states.products}
              showCheckbox
              selectedItems={selectedItems}
              onSelectAll={handleSelectAll}
              onSelectItem={handleSelectItem}
              getItemId={(item) => item.id}
              emptyMessage="No products found"
              loading={states.isLoading}
              minHeight="min-h-44"
            />
            <Pagination
              paginationData={states.pageInfo}
              onPageChange={(page) => handleChangePage(page)}
              onPageSizeChange={handleChangePageSize}
            />
          </Card>
        </div>
        <Card>
          <div className="space-y-6">
            <h2 className="text-lg font-semibold mb-4">Active dates</h2>

            <div className="grid grid-cols-2 gap-2">
              <FormField
                label="Start Date"
                type="date"
                defaultValue={
                  watch('discountStartDate')?.split('T')[0] || ''
                }
                onChange={(e) => {
                  const date = e.target.value;
                  const time =
                    watch('discountStartDate')?.split('T')[1] ||
                    '00:00';
                  setValue('discountStartDate', `${date}T${time}`);
                }}
                className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
              />
              <FormField
                label="Start Time"
                type="time"
                defaultValue={
                  watch('discountStartDate')?.split('T')[1] || '00:00'
                }
                onChange={(e) => {
                  const time = e.target.value;
                  const date =
                    watch('discountStartDate')?.split('T')[0] ||
                    new Date().toISOString().split('T')[0];
                  setValue('discountStartDate', `${date}T${time}`);
                }}
                className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <FormField
                label="End Date"
                type="date"
                defaultValue={
                  watch('discountEndDate')?.split('T')[0] || ''
                }
                onChange={(e) => {
                  const date = e.target.value;
                  const time =
                    watch('discountEndDate')?.split('T')[1] ||
                    '23:59';
                  setValue('discountEndDate', `${date}T${time}`);
                }}
                className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
              />

              {/* Time */}
              <FormField
                label="End Time"
                type="time"
                defaultValue={
                  watch('discountEndDate')?.split('T')[1] || '23:59'
                }
                onChange={(e) => {
                  const time = e.target.value;
                  const date =
                    watch('discountEndDate')?.split('T')[0] ||
                    new Date().toISOString().split('T')[0];
                  setValue('discountEndDate', `${date}T${time}`);
                }}
                className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
              />
            </div>
            <ErrorMessage message={dateError!} />
          </div>
        </Card>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(-1)}
            type="button" className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {isLoading ? <BtnLoader /> : 'Save'}
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default AddProductDiscount;
